/* ===========================================================================
 * NUVIK · product hero behaviour
 * ---------------------------------------------------------------------------
 * Registered against the shared section lifecycle (nuvik-section-lifecycle.js)
 * so it initialises on first page load AND on shopify:section:load, and tears
 * itself down on shopify:section:unload.
 *
 * This used to be an inline <script> in the section. Shopify's editor replaces
 * a section's HTML on re-render and does not re-run its page-load JavaScript,
 * so the inline version went inert after any setting change.
 *
 * All Liquid-derived values arrive through a JSON config block inside the
 * section root, so this file is static, cacheable, and testable off-Shopify.
 *
 * Every listener is bound with ctx.signal; every observer/timer is registered
 * with ctx.onCleanup. Repeated load/unload cycles therefore cannot accumulate
 * duplicate document/window handlers.
 * =========================================================================== */
(function () {
  'use strict';

  function readConfig(root) {
    var el = root.querySelector('[data-nvk-pdp-config]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); }
    catch (err) {
      if (window.console) console.error('[nuvik] bad pdp config', err);
      return null;
    }
  }

  function init(root, ctx) {
    var cfg = readConfig(root);
    if (!cfg) return;

    var $ = function (sel) { return root.querySelector(sel); };
    var $$ = function (sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); };

    var currency = cfg.currency;
    var savePrefix = cfg.savePrefix;
    var unitPrefix = cfg.unitPrefix;
    var soldOutText = cfg.soldOutText;
    var stickyButtonText = cfg.stickyButtonText;
    var showSlideLabel = cfg.showSlideLabel;
    var zoomOutLabel = cfg.zoomOutLabel;
    var zoomInLabel = cfg.zoomInLabel;
    var productHandle = cfg.productHandle;
    var showPriceInButton = cfg.showPriceInButton;

    /* Pending-callback tracking
       -------------------------
       This behaviour schedules UI work with setTimeout and requestAnimationFrame
       (price-change animation, lightbox transitions, add-to-cart feedback).
       Those are NOT cancelled by aborting the listener signal, so an unload
       while one is in flight used to run a callback against detached DOM.

       `later` / `frame` record their handles and are cleared on unload, and
       `alive()` guards any asynchronous continuation that a caller cannot
       cancel (an in-flight fetch, for example). */
    var timers = new Set();
    var frames = new Set();
    var destroyed = false;
    function alive() { return !destroyed; }
    function later(fn, ms) {
      var id = window.setTimeout(function () { timers.delete(id); if (alive()) fn(); }, ms);
      timers.add(id);
      return id;
    }
    function frame(fn) {
      var id = window.requestAnimationFrame(function () { frames.delete(id); if (alive()) fn(); });
      frames.add(id);
      return id;
    }
    ctx.onCleanup(function () {
      destroyed = true;
      timers.forEach(function (id) { window.clearTimeout(id); });
      frames.forEach(function (id) { window.cancelAnimationFrame(id); });
      timers.clear();
      frames.clear();
      /* A transition class is applied before a deferred callback removes it.
         Cancelling that callback would otherwise leave the class stuck on the
         element, so any visual state this instance applied is reverted here. */
      Array.prototype.forEach.call(
        root.querySelectorAll('.nvk-price-changing'),
        function (el) { el.classList.remove('nvk-price-changing'); }
      );
      root.classList.remove('is-loaded');
    });

    var discountLive = root.dataset.discountLive === 'true';
    var desktopStickyEnabled = root.dataset.desktopSticky === 'true';
    var mobileStickyEnabled = root.dataset.mobileSticky === 'true';

const money = cents => new Intl.NumberFormat('da-DK', { style: 'currency', currency }).format(cents / 100);

  const state = {
    activeSlide: 0,
    variant: null,
    bundleEl: null
  };

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  frame(() => {
    root.classList.add('is-loaded');
  });

  if ('IntersectionObserver' in window) {
    const revealObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          root.classList.add('is-visible');
          revealObserver.disconnect();
        }
      });
    }, { threshold: 0.18 });

    revealObserver.observe(root);
      ctx.onCleanup(function () { revealObserver.disconnect(); });
  } else {
    root.classList.add('is-visible');
  }

  const gallery = $('[data-nvk-gallery]');
  const track = $('[data-nvk-track]');
  const slides = $$('[data-nvk-slide]');
  const thumbs = $$('[data-nvk-thumb]');
  const dotsWrap = $('[data-nvk-dots]');
  const counter = $('[data-nvk-counter]');
  const zoomButton = $('[data-nvk-open-zoom]');
  const lightbox = $('[data-nvk-lightbox]');
  const lightboxImage = $('[data-nvk-lightbox-image]');
  const lightboxViewport = $('[data-nvk-lightbox-viewport]');
  const lightboxClose = $('[data-nvk-close-zoom]');
  const lightboxZoom = $('[data-nvk-lightbox-zoom]');
  const lightboxZoomLabel = $('[data-nvk-lightbox-zoom-label]');
  const lightboxCount = $('[data-nvk-lightbox-count]');
  let lightboxLastFocus = null;

  function goTo(index) {
    if (!track || !slides.length) return;

    state.activeSlide = (index + slides.length) % slides.length;
    track.style.transform = `translateX(-${state.activeSlide * 100}%)`;

    slides.forEach((slide, i) => {
      const on = i === state.activeSlide;
      slide.setAttribute('aria-hidden', on ? 'false' : 'true');

      const video = slide.querySelector('video');
      if (!video) return;

      if (on) {
        if (video.autoplay) video.play().catch(() => {});
      } else {
        video.pause();
      }
    });

    thumbs.forEach((t, i) => {
      const on = i === state.activeSlide;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-current', on ? 'true' : 'false');
    });

    $$('.nvk-gallery__dot').forEach((d, i) => {
      const on = i === state.activeSlide;
      d.classList.toggle('is-active', on);
      d.setAttribute('aria-current', on ? 'true' : 'false');
    });

    if (counter) counter.textContent = slides.length > 1 ? `${state.activeSlide + 1} / ${slides.length}` : '';

    if (zoomButton) {
      const activeImage = slides[state.activeSlide] && slides[state.activeSlide].querySelector('.nvk-slide__image');
      zoomButton.hidden = !activeImage;
    }
  }

  if (dotsWrap && slides.length > 1) {
    slides.forEach((_, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = `nvk-gallery__dot ${i === 0 ? 'is-active' : ''}`;
      dot.setAttribute('aria-label', showSlideLabel + ' ' + (i + 1));
      dot.addEventListener('click', () => goTo(i), { signal: ctx.signal });
      dotsWrap.appendChild(dot);
    });
  }

  thumbs.forEach((t, i) => t.addEventListener('click', () => goTo(i), { signal: ctx.signal }));

  if (gallery) {
    gallery.addEventListener('keydown', e => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goTo(state.activeSlide - 1);
      }

      if (e.key === 'ArrowRight') {
        e.preventDefault();
        goTo(state.activeSlide + 1);
      }
    }, { signal: ctx.signal });
  }

  function getLargestImageSource(image) {
    if (!image) return '';

    const srcset = image.getAttribute('srcset');
    if (!srcset) return image.currentSrc || image.src || '';

    const candidates = srcset
      .split(',')
      .map(item => {
        const parts = item.trim().split(/\s+/);
        const width = Number((parts[1] || '').replace('w', '')) || 0;
        return { src: parts[0], width };
      })
      .filter(item => item.src);

    candidates.sort((a, b) => b.width - a.width);
    return (candidates[0] && candidates[0].src) || image.currentSrc || image.src || '';
  }

  function setLightboxZoom(zoomed) {
    if (!lightbox) return;

    lightbox.classList.toggle('is-zoomed', zoomed);

    if (lightboxZoom) {
      lightboxZoom.setAttribute('aria-pressed', zoomed ? 'true' : 'false');
    }

    if (lightboxZoomLabel) {
      lightboxZoomLabel.textContent = zoomed
        ? zoomOutLabel
        : zoomInLabel;
    }

    if (!zoomed && lightboxViewport) {
      lightboxViewport.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }

  function openLightbox() {
    if (!lightbox || !lightboxImage || !slides.length) return;

    const activeSlide = slides[state.activeSlide];
    const activeImage = activeSlide && activeSlide.querySelector('.nvk-slide__image');
    if (!activeImage) return;

    lightboxLastFocus = document.activeElement;
    lightboxImage.src = getLargestImageSource(activeImage);
    lightboxImage.alt = activeImage.alt || '';

    if (lightboxCount) {
      lightboxCount.textContent = slides.length > 1 ? `${state.activeSlide + 1} / ${slides.length}` : '';
    }

    setLightboxZoom(false);
    lightbox.hidden = false;
    lightbox.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('nvk-lightbox-open');

    frame(() => {
      lightbox.classList.add('is-open');
      if (lightboxClose) lightboxClose.focus();
    });
  }

  function closeLightbox() {
    if (!lightbox || lightbox.hidden) return;

    lightbox.classList.remove('is-open', 'is-zoomed');
    lightbox.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('nvk-lightbox-open');

    later(() => {
      if (!lightbox.classList.contains('is-open')) {
        lightbox.hidden = true;
        lightboxImage.removeAttribute('src');
      }
    }, prefersReducedMotion ? 0 : 220);

    if (lightboxLastFocus && typeof lightboxLastFocus.focus === 'function') {
      lightboxLastFocus.focus();
    }
  }

  if (zoomButton) zoomButton.addEventListener('click', openLightbox, { signal: ctx.signal });
  if (lightboxClose) lightboxClose.addEventListener('click', closeLightbox, { signal: ctx.signal });

  if (lightboxZoom) {
    lightboxZoom.addEventListener('click', () => {
      setLightboxZoom(!lightbox.classList.contains('is-zoomed'));
    }, { signal: ctx.signal });
  }

  if (lightboxImage) {
    lightboxImage.addEventListener('click', () => {
      setLightboxZoom(!lightbox.classList.contains('is-zoomed'));
    }, { signal: ctx.signal });
  }

  if (lightbox) {
    lightbox.addEventListener('click', event => {
      if (event.target === lightbox) closeLightbox();
    }, { signal: ctx.signal });
  }

  document.addEventListener('keydown', event => {
    if (event.key === 'Escape' && lightbox && !lightbox.hidden) {
      event.preventDefault();
      closeLightbox();
    }
  }, { signal: ctx.signal });

  let startX = 0;
  let startY = 0;
  let dragging = false;
  let horizontal = false;
  let suppressImageClick = false;

  if (track) {
    track.addEventListener('pointerdown', e => {
      startX = e.clientX;
      startY = e.clientY;
      dragging = true;
      horizontal = false;
      if (track.setPointerCapture && e.pointerId != null) track.setPointerCapture(e.pointerId);
    }, { signal: ctx.signal });

    track.addEventListener('pointermove', e => {
      if (!dragging) return;

      const dx = e.clientX - startX;
      const dy = e.clientY - startY;

      if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) {
        horizontal = true;
      }
    }, { signal: ctx.signal });

    track.addEventListener('pointerup', e => {
      if (!dragging) return;

      const diff = e.clientX - startX;

      if (horizontal && Math.abs(diff) > 45) {
        suppressImageClick = true;
        goTo(diff < 0 ? state.activeSlide + 1 : state.activeSlide - 1);
        frame(() => {
          suppressImageClick = false;
        });
      }

      dragging = false;
    }, { signal: ctx.signal });

    track.addEventListener('pointercancel', () => {
      dragging = false;
    }, { signal: ctx.signal });

    track.addEventListener('click', event => {
      if (suppressImageClick) return;

      const image = event.target.closest('.nvk-slide__image');
      if (image) openLightbox();
    }, { signal: ctx.signal });
  }

  $$('[data-nvk-video-slide]').forEach(slide => {
    const video = slide.querySelector('video');
    const btn = slide.querySelector('[data-nvk-mute]');
    if (!video || !btn) return;

    const icon = btn.querySelector('[data-nvk-mute-on]');

    btn.addEventListener('click', () => {
      video.muted = !video.muted;
      btn.setAttribute('aria-pressed', video.muted ? 'false' : 'true');

      if (icon) {
        icon.textContent = video.muted ? '🔇' : '🔊';
      }
    }, { signal: ctx.signal });
  });

  if (cfg.variants && cfg.variants.length) {
    const variants = cfg.variants;

    const variantInput = $('[data-nvk-variant-id]');
    const quantityInput = $('[data-nvk-quantity]');
    const optionInputs = $$('[data-nvk-option]');
    const colorSwatches = $$('[data-nvk-color-swatch]');
    const colorPickers = $$('[data-nvk-color-picker]');
    const dynamicBundleFigures = $$('[data-nvk-dynamic-bundle-figure]');
    const stickyThumb = $('[data-nvk-sticky-thumb]');
    const bundleButtons = $$('[data-nvk-bundle]');
    const submitBtn = $('[data-nvk-submit]');
    const buttonLabel = $('[data-nvk-button-label]');
    const priceEl = $('[data-nvk-price]');
    const compareEl = $('[data-nvk-compare]');
    const saveBadge = $('[data-nvk-save-badge]');
    const unitHero = $('[data-nvk-unit-hero]');
    const sticky = $('[data-nvk-sticky]');
    const stickyBtn = $('[data-nvk-sticky-button]');
    const stickyPrice = $('[data-nvk-sticky-price]');
    const stickyLabel = $('[data-nvk-sticky-label]');
    const stickyToggle = $('[data-nvk-sticky-toggle]');
    const stickyBundleButtons = $$('[data-nvk-sticky-bundle]');
    const productForm = root.querySelector('form[action*="/cart/add"]') || root.querySelector('form');
    const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');

    state.variant =
      variants.find(v => Number(v.id) === Number(variantInput && variantInput.value) && v.available) ||
      variants.find(v => v.available) ||
      variants.find(v => Number(v.id) === Number(variantInput && variantInput.value)) ||
      variants[0] ||
      null;

    state.bundleEl =
      bundleButtons.find(b => b.classList.contains('is-active')) ||
      bundleButtons[0] ||
      null;

    function variantForOptions(options) {
      return variants.find(variant =>
        variant.options.every((option, index) => option === options[index])
      ) || null;
    }

    function optionInputByIndex(index) {
      return optionInputs.find(input =>
        Number(input.dataset.nvkOption) === Number(index)
      ) || null;
    }

    function activeColorLabel() {
      const active = colorSwatches.find(button => button.classList.contains('is-active'));
      return active ? (active.dataset.displayLabel || active.dataset.value || '') : '';
    }

    function syncColorUI() {
      if (!state.variant || !colorSwatches.length) return;

      const selectedOptions = state.variant.options.slice();

      colorPickers.forEach(picker => {
        const optionIndex = Number(picker.dataset.optionIndex);
        const buttons = colorSwatches.filter(button =>
          Number(button.dataset.optionIndex) === optionIndex
        );
        const selectedValue = state.variant.options[optionIndex];
        const labelNode = picker.querySelector('[data-nvk-color-label]');
        let displayLabel = selectedValue || '';

        buttons.forEach(button => {
          const candidateOptions = selectedOptions.slice();
          candidateOptions[optionIndex] = button.dataset.value;

          const candidate = variantForOptions(candidateOptions);
          const available = Boolean(candidate && candidate.available);
          const active = button.dataset.value === selectedValue;
          const status = button.querySelector('[data-nvk-color-status]');

          button.classList.toggle('is-active', active);
          button.setAttribute('aria-checked', active ? 'true' : 'false');
          button.setAttribute('aria-disabled', available ? 'false' : 'true');
          button.tabIndex = active ? 0 : -1;
          button.disabled = !available;

          if (active) {
            displayLabel = button.dataset.displayLabel || button.dataset.value || displayLabel;
          }

          if (status) {
            status.textContent = available ? '' : soldOutText;
          }
        });

        if (labelNode) {
          labelNode.textContent = displayLabel;
        }
      });
    }

    function syncOptionInputsToVariant() {
      if (!state.variant || !optionInputs.length) return;

      optionInputs.forEach(input => {
        const index = Number(input.dataset.nvkOption);

        if (state.variant.options[index] != null) {
          input.value = state.variant.options[index];
        }
      });

      syncColorUI();
    }

    syncOptionInputsToVariant();

    const bQty = b => {
      const qty = parseInt((b && b.dataset.qty) || '1', 10);
      return Number.isFinite(qty) && qty > 0 ? qty : 1;
    };

    const bDisc = b => discountLive ? Number((b && b.dataset.discount) || 0) : 0;
    const bBefore = (unit, b) => unit * bQty(b);
    const bTotal = (unit, b) => Math.round(unit * bQty(b) * ((100 - bDisc(b)) / 100));
    const bUnit = (unit, b) => Math.round(bTotal(unit, b) / bQty(b));
    const bSaved = (unit, b) => bBefore(unit, b) - bTotal(unit, b);

    function activeQty() {
      return state.bundleEl ? bQty(state.bundleEl) : 1;
    }

    function syncFormFields() {
      const qty = activeQty();

      if (variantInput && state.variant) {
        variantInput.value = state.variant.id;
        variantInput.disabled = false;
      }

      if (quantityInput) {
        quantityInput.value = String(qty);
      }

      if (window.Shopify && Shopify.designMode) {
        console.info('[Nuvik PDP submit data]', {
          product: productHandle,
          variantId: state.variant ? state.variant.id : null,
          variantTitle: state.variant ? state.variant.title : null,
          available: state.variant ? state.variant.available : null,
          quantity: qty
        });
      }
    }

    function pulseElements(elements, updateFn) {
      const active = elements.filter(Boolean);

      if (prefersReducedMotion || !active.length) {
        updateFn();
        return;
      }

      active.forEach(el => el.classList.add('nvk-price-changing'));

      later(() => {
        updateFn();
        frame(() => {
          active.forEach(el => el.classList.remove('nvk-price-changing'));
        });
      }, 110);
    }

    function render() {
      const v = state.variant;
      if (!v) return;

      const b = state.bundleEl;

      syncColorUI();

      if (v.featured_image) {
        dynamicBundleFigures.forEach(figure => {
          const image = figure.querySelector('img');
          if (!image) return;

          image.removeAttribute('srcset');
          image.removeAttribute('sizes');
          image.src = v.featured_image;
        });

        if (stickyThumb) {
          stickyThumb.removeAttribute('srcset');
          stickyThumb.src = v.featured_image;
        }
      }

      bundleButtons.forEach(card => {
        const tEl = card.querySelector('[data-bundle-price]');
        const sEl = card.querySelector('[data-bundle-saved]');
        const cEl = card.querySelector('[data-bundle-compare]');
        const saved = bSaved(v.price, card);
        const active = card === b;

        card.classList.remove('is-disabled');
        card.classList.toggle('is-active', active);
        card.setAttribute('aria-disabled', 'false');
        card.setAttribute('aria-checked', active ? 'true' : 'false');
        card.setAttribute('tabindex', active ? '0' : '-1');

        if (tEl) {
          tEl.textContent = money(bTotal(v.price, card));
        }

        // Saving UI is driven by `hidden` (not inline display) so that the
        // server-rendered state and the client state use one mechanism.
        // `saved` is already forced to 0 when the discount gate is closed.
        if (sEl) {
          if (saved > 0) {
            sEl.textContent = `${savePrefix} ${money(saved)}`;
            sEl.hidden = false;
          } else {
            sEl.hidden = true;
          }
        }

        if (cEl) {
          if (saved > 0) {
            cEl.textContent = money(bBefore(v.price, card));
            cEl.hidden = false;
          } else {
            cEl.hidden = true;
          }
        }
      });

      const hasBundles = bundleButtons.length > 0;
      const total = hasBundles && b ? bTotal(v.price, b) : v.price;
      const before = hasBundles && b ? bBefore(v.price, b) : v.price;
      const saved = hasBundles && b ? bSaved(v.price, b) : 0;
      const unit = hasBundles && b ? bUnit(v.price, b) : v.price;
      const discount = hasBundles && b ? bDisc(b) : 0;
      const label = hasBundles && b ? ((b.querySelector('.nvk__tile-label') || {}).textContent || '1x') : '1x';
      const canBuy = Boolean(v.available);

      pulseElements([priceEl, unitHero, stickyPrice], () => {
        if (priceEl) {
          priceEl.textContent = money(total);
        }

        if (unitHero) {
          unitHero.textContent = `${unitPrefix} ${money(unit)}/stk`;
        }

        if (stickyPrice) {
          stickyPrice.textContent = money(total);
        }
      });

      if (compareEl) {
        if (saved > 0) {
          compareEl.textContent = money(before);
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }

      if (saveBadge) {
        if (discount > 0) {
          saveBadge.textContent = `${savePrefix} ${discount}%`;
          saveBadge.hidden = false;
        } else {
          saveBadge.hidden = true;
        }
      }

      if (submitBtn && buttonLabel && !submitBtn.classList.contains('is-loading') && !submitBtn.classList.contains('is-added')) {
        submitBtn.disabled = !canBuy;

        if (!v.available) {
          buttonLabel.textContent = soldOutText;
        } else {
          const base = submitBtn.dataset.buttonBase || 'Køb nu';
          const showPrice = showPriceInButton;
          buttonLabel.textContent = showPrice ? `${base} · ${money(total)}` : base;
        }
      }

      if (stickyLabel) {
        const summaryParts = [];
        const colorLabel = activeColorLabel();

        if (colorLabel) summaryParts.push(colorLabel);
        if (label) summaryParts.push(label);
        if (saved > 0) summaryParts.push(`${savePrefix} ${money(saved)}`);

        stickyLabel.textContent = summaryParts.join(' · ');
      }

      if (stickyBtn && !stickyBtn.classList.contains('is-loading') && !stickyBtn.classList.contains('is-added')) {
        stickyBtn.disabled = !canBuy;
        stickyBtn.textContent = !v.available ? soldOutText : stickyButtonText;
      }

      stickyBundleButtons.forEach(stickyBundle => {
        const index = Number(stickyBundle.dataset.nvkStickyBundle);
        const linkedBundle = bundleButtons[index];

        if (!linkedBundle) {
          stickyBundle.hidden = true;
          return;
        }

        const active = linkedBundle === b;
        const savedAmount = bSaved(v.price, linkedBundle);
        const priceNode = stickyBundle.querySelector('[data-sticky-bundle-price]');
        const saveNode = stickyBundle.querySelector('[data-sticky-bundle-save]');

        stickyBundle.classList.toggle('is-active', active);
        stickyBundle.setAttribute('aria-pressed', active ? 'true' : 'false');

        if (priceNode) {
          priceNode.textContent = money(bTotal(v.price, linkedBundle));
        }

        if (saveNode) {
          if (savedAmount > 0) {
            saveNode.hidden = false;
            saveNode.textContent = `${savePrefix} ${money(savedAmount)}`;
          } else {
            saveNode.hidden = true;
          }
        }
      });

      syncFormFields();
    }

    function selectBundle(card) {
      if (!card) return;

      state.bundleEl = card;
      render();
    }

    bundleButtons.forEach((card, index) => {
      card.addEventListener('click', () => selectBundle(card), { signal: ctx.signal });

      card.addEventListener('keydown', e => {
        const keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
        if (!keys.includes(e.key)) return;

        e.preventDefault();

        const direction = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : -1;
        const nextIndex = (index + direction + bundleButtons.length) % bundleButtons.length;
        const next = bundleButtons[nextIndex];

        selectBundle(next);
        next.focus();
      }, { signal: ctx.signal });
    });

    if (stickyToggle && sticky) {
      stickyToggle.addEventListener('click', () => {
        const expanded = sticky.classList.toggle('is-expanded');
        stickyToggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      }, { signal: ctx.signal });
    }

    stickyBundleButtons.forEach(stickyBundle => {
      stickyBundle.addEventListener('click', () => {
        const index = Number(stickyBundle.dataset.nvkStickyBundle);
        const linkedBundle = bundleButtons[index];

        if (!linkedBundle) return;

        selectBundle(linkedBundle);

        if (sticky && stickyToggle) {
          sticky.classList.remove('is-expanded');
          stickyToggle.setAttribute('aria-expanded', 'false');
        }
      }, { signal: ctx.signal });
    });

    document.addEventListener('click', event => {
      if (!sticky || !stickyToggle) return;
      if (sticky.contains(event.target)) return;

      sticky.classList.remove('is-expanded');
      stickyToggle.setAttribute('aria-expanded', 'false');
    }, { signal: ctx.signal });

    function updateVariant() {
      if (!variants.length) return;

      const chosen = optionInputs.map(input => input.value);
      const match = optionInputs.length ? variantForOptions(chosen) : state.variant;

      if (!match) {
        syncColorUI();
        return;
      }

      state.variant = match;
      syncOptionInputsToVariant();
      render();

      if (match.featured_media && match.featured_media.id) {
        const idx = slides.findIndex(slide =>
          Number(slide.dataset.mediaId) === Number(match.featured_media.id)
        );

        if (idx >= 0) {
          goTo(idx);
        }
      }

      if (!(window.Shopify && Shopify.designMode) && window.history && window.URL) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', String(match.id));
        window.history.replaceState({}, '', url.toString());
      }
    }

    optionInputs.forEach(input => {
      input.addEventListener('change', updateVariant, { signal: ctx.signal });
    });

    colorSwatches.forEach(button => {
      button.addEventListener('click', () => {
        if (button.disabled) return;

        const optionIndex = Number(button.dataset.optionIndex);
        const input = optionInputByIndex(optionIndex);

        if (!input) return;

        input.value = button.dataset.value;
        updateVariant();
      }, { signal: ctx.signal });

      button.addEventListener('keydown', event => {
        const keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
        if (!keys.includes(event.key)) return;

        event.preventDefault();

        const optionIndex = Number(button.dataset.optionIndex);
        const group = colorSwatches.filter(item =>
          Number(item.dataset.optionIndex) === optionIndex && !item.disabled
        );

        if (!group.length) return;

        const currentIndex = Math.max(0, group.indexOf(button));
        const direction =
          event.key === 'ArrowRight' || event.key === 'ArrowDown'
            ? 1
            : -1;
        const next = group[(currentIndex + direction + group.length) % group.length];

        next.focus();
        next.click();
      }, { signal: ctx.signal });
    });

    function setButtonState(stateName) {
      if (!submitBtn || !buttonLabel) return;

      submitBtn.classList.remove('is-loading', 'is-added');

      if (stickyBtn) {
        stickyBtn.classList.remove('is-loading', 'is-added');
      }

      if (stateName === 'loading') {
        submitBtn.classList.add('is-loading');
        submitBtn.disabled = true;
        buttonLabel.textContent = 'Tilføjer...';

        if (stickyBtn) {
          stickyBtn.classList.add('is-loading');
          stickyBtn.disabled = true;
          stickyBtn.textContent = 'Tilføjer...';
        }
      }

      if (stateName === 'added') {
        submitBtn.classList.add('is-added');
        submitBtn.disabled = true;
        buttonLabel.textContent = 'Tilføjet';

        if (stickyBtn) {
          stickyBtn.classList.add('is-added');
          stickyBtn.disabled = true;
          stickyBtn.textContent = 'Tilføjet';
        }
      }

      if (stateName === 'default') {
        submitBtn.disabled = false;

        if (stickyBtn) {
          stickyBtn.disabled = false;
        }

        render();
      }
    }

    if (productForm) {
      productForm.addEventListener('submit', async e => {
        e.preventDefault();
        e.stopPropagation();

        syncFormFields();

        const variantId = variantInput ? variantInput.value : '';
        const quantity = quantityInput ? quantityInput.value : '1';

        if (
          !variantId ||
          !quantity ||
          Number(quantity) < 1 ||
          !state.variant ||
          !state.variant.available
        ) {
          render();
          alert(soldOutText || 'Produktet er udsolgt');
          return;
        }

        setButtonState('loading');

        try {
          const sections = cart && typeof cart.getSectionsToRender === 'function'
            ? cart.getSectionsToRender().map(section => section.id)
            : ['cart-drawer', 'cart-icon-bubble'];

          const response = await fetch('/cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
              items: [
                {
                  id: Number(variantId),
                  quantity: Number(quantity)
                }
              ],
              sections: sections,
              sections_url: window.location.pathname
            })
          });

          const data = await response.json();

          /* The instance may have been torn down while the request was in
             flight. A fetch cannot be un-sent and aborting it would NOT undo a
             cart mutation the server already applied, so we deliberately do not
             abort it - we simply stop touching detached DOM and let the cart
             state stand. */
          if (!alive()) return;

          if (!response.ok) {
            console.error('[Nuvik Shopify add error]', data);
            if (!alive()) return;
            alert(data.description || data.message || 'Kunne ikke tilføje til kurv');
            setButtonState('default');
            return;
          }

          setButtonState('added');

          later(() => {
            if (cart && typeof cart.renderContents === 'function' && data.sections) {
              if (typeof cart.setActiveElement === 'function') {
                cart.setActiveElement(document.activeElement);
              }

              cart.classList.remove('is-empty');
              cart.renderContents(data);
            } else {
              window.location.href = '/cart';
            }

            later(() => {
              setButtonState('default');
            }, 500);
          }, prefersReducedMotion ? 0 : 420);
        } catch (error) {
          /* The instance may have been torn down while the request was in
             flight - a section re-render, or navigation. Touching detached DOM
             or alerting the shopper about a section that no longer exists is
             wrong, so the failure is logged and nothing else happens.
             Note: the request is NOT aborted. Aborting would not undo a cart
             mutation the server may already have applied. */
          console.error('[Nuvik network error]', error);
          if (!alive()) return;
          alert('Der skete en fejl. Prøv igen.');
          setButtonState('default');
        }
      }, { signal: ctx.signal });
    }

    if (submitBtn) {
      submitBtn.addEventListener('click', syncFormFields, { signal: ctx.signal });
    }

    if (stickyBtn && productForm) {
      stickyBtn.addEventListener('click', () => {
        syncFormFields();

        if (!state.variant || !state.variant.available) {
          render();
          alert(soldOutText || 'Produktet er udsolgt');
          return;
        }

        if (typeof productForm.requestSubmit === 'function') {
          productForm.requestSubmit();
        } else if (submitBtn) {
          submitBtn.click();
        }
      }, { signal: ctx.signal });
    }

    function syncStickyVisibility(isSubmitVisible) {
      if (!sticky) return;

      const desktopAllowed = desktopStickyEnabled && window.innerWidth > 900;
      const mobileAllowed = mobileStickyEnabled && window.innerWidth <= 900;
      const allowed = desktopAllowed || mobileAllowed;

      sticky.classList.toggle('is-visible', allowed && !isSubmitVisible);
    }

    if (sticky && submitBtn && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver(entries => {
        entries.forEach(en => {
          syncStickyVisibility(en.isIntersecting);
        });
      }, { threshold: 0, rootMargin: '0px 0px -40px 0px' });

      io.observe(submitBtn);
      ctx.onCleanup(function () { io.disconnect(); });
      window.addEventListener('resize', () => {
        const rect = submitBtn.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
        syncStickyVisibility(isVisible);
      }, { signal: ctx.signal });
    }

    syncOptionInputsToVariant();
    render();
    syncFormFields();
  }

  goTo(0);
  }

  /* Page-level state this section can set must be released on unload, or a
     re-render while the lightbox is open would leave the document permanently
     scroll-locked. */
  function destroy(root) {
    var lightbox = root.querySelector('[data-nvk-lightbox]');
    if (lightbox) {
      lightbox.classList.remove('is-open', 'is-zoomed');
      lightbox.hidden = true;
    }
    if (!document.querySelector('[data-nvk-lightbox].is-open')) {
      document.documentElement.classList.remove('nvk-lightbox-open');
    }
  }

  function boot() {
    if (!window.NuvikSections) return;
    window.NuvikSections.register('nuvikproducthero', { init: init, destroy: destroy });
  }

  if (window.NuvikSections) boot();
  else document.addEventListener('DOMContentLoaded', boot);
})();
